import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PromoBanner from '../components/PromoBanner';
import SEO from '../components/SEO';
import { seoData } from '../data/seoData';

const CloudCareerQuizPage = () => {
  const [state, setState] = useState({ responses: Array(15).fill(null) });
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState(false);

  const QUESTIONS = [
    { t: "I enjoy meeting customers or stakeholders to understand goals.", dims: { CF: 1 } },
    { t: "I like explaining technical trade-offs to non-technical people.", dims: { CF: 1 } },
    { t: "I like writing code or scripts to automate tasks.", dims: { CODE: 1 } },
    { t: "I want to manage infrastructure with code (Terraform, CDK, etc.).", dims: { CODE: 1 } },
    { t: "I like diagnosing outages and performance issues.", dims: { OPS: 1 } },
    { t: "I'm okay with on-call or time-sensitive troubleshooting.", dims: { OPS: 1 } },
    { t: "I'm interested in identity, policies, and securing systems.", dims: { SEC: 1 } },
    { t: "Compliance and guardrails sound interesting to me.", dims: { SEC: 1 } },
    { t: "Working with datasets and SQL sounds fun.", dims: { DATA: 1 } },
    { t: "I like designing pipelines to move and transform data.", dims: { DATA: 1 } },
    { t: "I want to build with ML/AI services or frameworks.", dims: { ML: 1 } },
    { t: "I'm curious about deploying and monitoring ML models.", dims: { ML: 1 } },
    { t: "I like automating deployments and CI/CD pipelines.", dims: { DEVOPS: 1 } },
    { t: "I enjoy high-level system design and trade-offs.", dims: { ARCH: 1 } },
    { t: "I like drawing architectures before building.", dims: { ARCH: 1 } }
  ];

  const ANSWERS = [
    { label: "Not for me", score: 0 },
    { label: "Neutral", score: 1 },
    { label: "Like it", score: 2 },
    { label: "Love it", score: 3 }
  ];

  const ROLES = {
    "Solutions Architect": { dims: { CF: 2, ARCH: 3 }, desc: "Designs end-to-end architectures and solutions by communicating with stakeholders." },
    "Cloud Engineer": { dims: { CODE: 2, OPS: 2 }, desc: "Builds and manages cloud infrastructure and automation." },
    "Cloud Support Engineer": { dims: { OPS: 3, CF: 1 }, desc: "Supports customers in troubleshooting and issue resolution." },
    "Security Engineer": { dims: { SEC: 3, OPS: 1 }, desc: "Secures cloud systems and ensures compliance." },
    "DevOps Engineer": { dims: { DEVOPS: 3, CODE: 2 }, desc: "Automates deployments and manages CI/CD pipelines." },
    "Data Engineer": { dims: { DATA: 3, CODE: 1 }, desc: "Builds and optimizes data pipelines and transformations." },
    "AI / ML Engineer": { dims: { ML: 3, DATA: 1 }, desc: "Develops and deploys ML models and AI solutions." },
    "Cloud Architect": { dims: { ARCH: 3, CF: 1 }, desc: "Manages and implements an organization's cloud computing strategy." }
  };

  const handleAnswerChange = (questionIndex, answerIndex) => {
    const newResponses = [...state.responses];
    newResponses[questionIndex] = answerIndex;
    setState({ responses: newResponses });
    setError(false);
  };

  const computeResults = () => {
    const dimScores = { CF: 0, CODE: 0, OPS: 0, SEC: 0, DATA: 0, ML: 0, DEVOPS: 0, ARCH: 0 };
    const dimMax = {};

    QUESTIONS.forEach(q => {
      for (const d in q.dims) {
        dimMax[d] = (dimMax[d] || 0) + 3 * q.dims[d];
      }
    });

    Object.keys(dimScores).forEach(d => {
      if (!(d in dimMax)) dimMax[d] = 0;
    });

    QUESTIONS.forEach((q, qi) => {
      const ans = state.responses[qi];
      if (ans !== null) {
        const score = ANSWERS[ans].score;
        for (const d in q.dims) {
          dimScores[d] += score * q.dims[d];
        }
      }
    });

    const results = [];
    for (const role in ROLES) {
      let acc = 0, denom = 0;
      const weights = ROLES[role].dims;
      for (const d in weights) {
        const w = weights[d];
        acc += (dimScores[d] || 0) * w;
        denom += (dimMax[d] || 0) * w;
      }
      const pct = denom > 0 ? Math.min(100, Math.round(acc / denom * 100)) : 0;
      results.push({ role, score: pct, desc: ROLES[role].desc });
    }

    results.sort((a, b) => b.score - a.score);
    return results.slice(0, 3);
  };

  const handleSubmit = () => {
    if (state.responses.includes(null)) {
      setError(true);
    } else {
      setError(false);
      setShowResults(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleReset = () => {
    setState({ responses: Array(15).fill(null) });
    setError(false);
    setShowResults(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const progress = state.responses.filter(v => v !== null).length;
  const results = showResults ? computeResults() : [];

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#eef0fa', 
      color: '#1e293b',
      fontFamily: 'system-ui,-apple-system,Segoe UI,Roboto,Ubuntu,Cantarell,Noto Sans,Helvetica,Arial,"Apple Color Emoji","Segoe UI Emoji"'
    }}>
      <SEO {...seoData.cloudRoleQuiz} />
      <PromoBanner />
      <Navigation />
      
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '100px 20px 20px 20px' }}>
        <header style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ fontSize: '28px', marginRight: '8px' }}>☁️</div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ margin: 0 }}>Cloud Career Quiz</h1>
          </div>
        </header>

        {!showResults ? (
          <>
            <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 6px rgba(0,0,0,.1)', marginBottom: '20px' }}>
              <h2 style={{ margin: '0 0 16px 0' }}>Want to find the right cloud role for you?</h2>
              <p style={{ margin: '0 0 16px 0' }}>Answer 15 quick questions. At the end, you'll see your top 3 matching cloud roles with descriptions.</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', margin: '12px 0' }}>
                <span style={{ background: '#f6f6fb', padding: '6px 10px', borderRadius: '20px', fontSize: '14px' }}>🧑‍💻 Solutions Architect</span>
                <span style={{ background: '#f6f6fb', padding: '6px 10px', borderRadius: '20px', fontSize: '14px' }}>🧑‍🔧 Cloud Engineer</span>
                <span style={{ background: '#f6f6fb', padding: '6px 10px', borderRadius: '20px', fontSize: '14px' }}>🙋‍♀️ Cloud Support Engineer</span>
                <span style={{ background: '#f6f6fb', padding: '6px 10px', borderRadius: '20px', fontSize: '14px' }}>🧑‍💼 Security Engineer</span>
                <span style={{ background: '#f6f6fb', padding: '6px 10px', borderRadius: '20px', fontSize: '14px' }}>👩‍💻 DevOps Engineer</span>
                <span style={{ background: '#f6f6fb', padding: '6px 10px', borderRadius: '20px', fontSize: '14px' }}>👩‍🏫 Data Engineer</span>
                <span style={{ background: '#f6f6fb', padding: '6px 10px', borderRadius: '20px', fontSize: '14px' }}>👩‍🔬 AI / ML Engineer</span>
                <span style={{ background: '#f6f6fb', padding: '6px 10px', borderRadius: '20px', fontSize: '14px' }}>👷‍♀️ Cloud Architect</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px', gap: '8px' }}>
                <div style={{ flex: 1, height: '10px', background: '#e2e8f0', borderRadius: '999px', overflow: 'hidden' }}>
                  <div style={{ display: 'block', height: '100%', background: 'linear-gradient(90deg,#4f46e5,#6366f1)', width: `${(progress / 15) * 100}%` }}></div>
                </div>
                <div style={{ fontSize: '14px', color: '#64748b' }}>{progress} / 15</div>
              </div>
            </div>

            <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 6px rgba(0,0,0,.1)', marginBottom: '20px' }}>
              {QUESTIONS.map((q, qi) => (
                <div key={qi} style={{ margin: '16px 0', padding: '14px', border: '1px solid #cbd5e1', borderRadius: '10px', background: '#f6f6fb' }}>
                  <h3 style={{ margin: '0 0 8px 0' }}>Q{qi + 1}. {q.t}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                    {ANSWERS.map((a, ai) => (
                      <div key={ai} style={{ position: 'relative' }}>
                        <input
                          type="radio"
                          name={`q${qi}`}
                          value={ai}
                          id={`q${qi}_a${ai}`}
                          checked={state.responses[qi] === ai}
                          onChange={() => handleAnswerChange(qi, ai)}
                          style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                        />
                        <label
                          htmlFor={`q${qi}_a${ai}`}
                          style={{
                            display: 'block',
                            padding: '10px 8px',
                            border: state.responses[qi] === ai ? '1px solid #4f46e5' : '1px solid #cbd5e1',
                            borderRadius: '8px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: state.responses[qi] === ai ? '#1e293b' : '#334155',
                            background: state.responses[qi] === ai ? 'rgba(99,102,241,.1)' : '#fff',
                            fontWeight: state.responses[qi] === ai ? '600' : 'normal',
                            userSelect: 'none',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            minHeight: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {a.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
                <button
                  onClick={handleSubmit}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '600',
                    background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
                    color: '#fff'
                  }}
                >
                  Submit Quiz
                </button>
                <button
                  onClick={handleReset}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    cursor: 'pointer',
                    fontWeight: '600',
                    background: '#fff',
                    color: '#1e293b'
                  }}
                >
                  Reset
                </button>
              </div>
              
              {error && (
                <div style={{ color: '#dc2626', fontSize: '14px', marginTop: '10px', textAlign: 'center' }}>
                  Please answer all questions before submitting.
                </div>
              )}
            </div>
          </>
        ) : (
          <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 6px rgba(0,0,0,.1)', marginBottom: '20px' }}>
            <h2 style={{ margin: '0 0 20px 0' }}>Your Top 3 Matches</h2>
            {results.map((r, index) => (
              <div key={index} style={{ padding: '12px', border: '1px solid #cbd5e1', borderRadius: '10px', margin: '10px 0', background: '#f6f6fb' }}>
                <strong>{r.role}</strong>
                <div style={{ marginTop: '6px', fontWeight: '500' }}>{r.score}% match</div>
                <p style={{ margin: '6px 0' }}>{r.desc}</p>
                <div style={{ height: '10px', background: '#e2e8f0', borderRadius: '999px', overflow: 'hidden', marginTop: '6px' }}>
                  <div style={{ display: 'block', height: '100%', background: 'linear-gradient(90deg,#16a34a,#6366f1)', width: `${r.score}%` }}></div>
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
              <button
                onClick={handleReset}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
                  color: '#fff'
                }}
              >
                Start over
              </button>
            </div>
            <div style={{ marginTop: '20px', padding: '10px 16px 20px 16px', border: '1px solid #cbd5e1', borderRadius: '10px', background: '#f0f0ff', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 10px 0' }}>Ready to start learning cloud?</h3>
              <a
                href="/courses"
                style={{
                  display: 'inline-block',
                  marginTop: '10px',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
                  color: '#fff',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                👉 Zero To Cloud Courses
              </a>
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default CloudCareerQuizPage;